import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import Breadcrumb from "@/components/feature/Breadcrumb";
import EpisodeSection from "@/pages/detail/components/EpisodeSection";
import ShareModal from "@/pages/detail/components/ShareModal";
import RecommendList from "@/pages/detail/components/RecommendList";
import { movies } from "@/mocks/movies";

export default function Detail() {
  const { id } = useParams<{ id: string }>();
  const item = movies.find((m) => m.id === id);
  const [shareOpen, setShareOpen] = useState(false);

  useEffect(() => {
    if (item) document.title = `${item.title} - 剧情介绍与观看 - 邻居的妻子`;
    return () => {
      document.title = "邻居的妻子影视大全_剧情介绍与最新剧集观看";
    };
  }, [item]);

  if (!item) {
    return (
      <div className="w-full px-4 md:px-8 py-20 flex flex-col items-center justify-center min-h-[60vh]">
        <span className="w-20 h-20 flex items-center justify-center rounded-full bg-background-100 text-foreground-400">
          <i className="ri-error-warning-line text-4xl"></i>
        </span>
        <h1 className="mt-5 font-heading text-2xl font-bold text-foreground-950">影视作品不存在或已下架</h1>
        <p className="mt-2 text-sm text-foreground-500">你访问的作品可能已被移除，或链接有误</p>
        <Link
          to="/"
          className="mt-6 whitespace-nowrap inline-flex items-center gap-2 px-5 py-2.5 rounded-md bg-accent-500 text-primary-950 text-sm font-semibold hover:bg-accent-400 transition-colors cursor-pointer"
        >
          <i className="ri-home-4-line"></i>
          返回首页
        </Link>
      </div>
    );
  }

  const infoRows = [
    { label: "导演", value: item.director },
    { label: "主演", value: item.actors.join(" / ") },
    { label: "类型", value: `${item.category} · ${item.genre}` },
    { label: "年份", value: `${item.year}` },
    { label: "地区", value: item.region },
    { label: "状态", value: item.updateStatus },
  ];

  return (
    <div className="w-full">
      <div className="relative bg-primary-950">
        <img
          src={item.cover}
          alt=""
          aria-hidden="true"
          className="absolute inset-0 w-full h-full object-top opacity-20 blur-sm"
        />
        <div className="absolute inset-0 bg-gradient-to-t from-primary-950 via-primary-950/85 to-primary-950/70"></div>

        <div className="relative w-full px-4 md:px-8 pt-6 pb-10">
          <Breadcrumb items={[{ label: "影视分类", to: "/category" }, { label: item.title }]} />

          <div className="flex flex-col sm:flex-row gap-6 md:gap-8">
            <div className="w-40 md:w-56 shrink-0 mx-auto sm:mx-0">
              <div className="w-full aspect-[2/3] rounded-lg overflow-hidden border border-background-200/20">
                <img
                  src={item.cover}
                  alt={`${item.title} 海报`}
                  title={`${item.title} - 邻居的妻子影视大全`}
                  className="w-full h-full object-top"
                />
              </div>
            </div>

            <div className="flex-1 min-w-0 text-center sm:text-left">
              <div className="flex flex-wrap items-center justify-center sm:justify-start gap-2 mb-3">
                <span className="px-2.5 py-0.5 rounded-md bg-accent-500 text-primary-950 text-xs font-bold">
                  {item.category}
                </span>
                <span className="px-2.5 py-0.5 rounded-md bg-primary-800 text-accent-300 text-xs font-medium">
                  {item.genre}
                </span>
                <span className="px-2.5 py-0.5 rounded-md bg-primary-800 text-background-200 text-xs">
                  {item.region} · {item.year}
                </span>
              </div>

              <h1 className="font-heading text-2xl md:text-4xl font-bold text-background-50">{item.title}</h1>

              <div className="mt-3 flex items-center justify-center sm:justify-start gap-4">
                <span className="flex items-center gap-1 text-accent-400">
                  <i className="ri-star-fill"></i>
                  <strong className="text-lg md:text-xl">{item.rating.toFixed(1)}</strong>
                  <span className="text-xs text-background-300">/ 10</span>
                </span>
                <span className="text-sm text-background-300 flex items-center gap-1.5">
                  <i className="ri-refresh-line"></i>
                  {item.updateStatus}
                </span>
              </div>

              <p className="mt-4 text-sm md:text-base text-background-200/90 leading-relaxed line-clamp-3 max-w-2xl">
                {item.synopsis}
              </p>

              <div className="mt-6 flex flex-wrap items-center justify-center sm:justify-start gap-3">
                <button
                  type="button"
                  onClick={() => setShareOpen(true)}
                  className="whitespace-nowrap inline-flex items-center gap-2 px-5 py-2.5 rounded-md bg-accent-500 text-primary-950 text-sm font-semibold hover:bg-accent-400 transition-colors cursor-pointer"
                >
                  <i className="ri-share-forward-line"></i>
                  分享作品
                </button>
                <Link
                  to="/category"
                  className="whitespace-nowrap inline-flex items-center gap-2 px-5 py-2.5 rounded-md border border-background-300/40 text-background-50 text-sm font-medium hover:bg-background-50/10 transition-colors cursor-pointer"
                >
                  <i className="ri-grid-line"></i>
                  更多影视
                </Link>
              </div>
            </div>
          </div>
        </div>
      </div>

      <div className="w-full px-4 md:px-8 py-8 md:py-10">
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8 lg:gap-10">
          <div className="lg:col-span-2">
            <section>
              <h2 className="font-heading text-lg md:text-xl font-bold text-foreground-950 flex items-center gap-2 mb-4">
                <span className="w-1 h-5 rounded-full bg-accent-500 inline-block"></span>
                剧情介绍
              </h2>
              <p className="text-sm md:text-base text-foreground-700 leading-loose">{item.synopsis}</p>
            </section>

            <EpisodeSection item={item} />
          </div>

          <aside className="lg:col-span-1">
            <div className="rounded-lg bg-background-100 border border-background-200 p-5">
              <h2 className="font-heading text-base font-bold text-foreground-950 mb-4">详细资料</h2>
              <dl className="space-y-3">
                {infoRows.map((r) => (
                  <div key={r.label} className="flex gap-3 text-sm">
                    <dt className="w-14 shrink-0 text-foreground-500">{r.label}</dt>
                    <dd className="text-foreground-800 leading-relaxed">{r.value}</dd>
                  </div>
                ))}
              </dl>
              <div className="mt-4 pt-4 border-t border-background-200 flex flex-wrap gap-2">
                {item.tags.map((t) => (
                  <span key={t} className="px-2.5 py-1 rounded-full bg-secondary-100 text-secondary-900 text-xs">
                    {t}
                  </span>
                ))}
              </div>
            </div>

            <RecommendList current={item} pool={movies} />
          </aside>
        </div>
      </div>

      <ShareModal open={shareOpen} onClose={() => setShareOpen(false)} title={item.title} />
    </div>
  );
}